import {DialogBody, DialogHeader} from '@cypro/ui/components'
import {useDebouncedState} from '@cypro/ui/hooks/use-debounced-state'
import {useAppForm} from '@cypro/ui/integrations/form'
import {VStack, Icon, Text, Button} from '@chakra-ui/react'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {useSearchBatches} from '../../api/use-search-batches'
import {LuSearch} from 'react-icons/lu'
import {getYearMonthFormatterUTC} from '@cypro/ui/utils'
import {dialog} from '@/modules/layout'
import {createColumnHelper, getCoreRowModel, useReactTable, type ColumnDef} from '@tanstack/react-table'
import {DataTable} from '@cypro/ui/components/ui/data-table'
import {useMemo} from 'react'
import {Batch} from '@cypro/engine/schema'

function onClose() {
  dialog.close('add-batch-dialog')
}

const columnHelper = createColumnHelper<Batch>()

export interface AddBatchDialogProps {
  prescribedNameIds?: string[]
  isConsumable?: boolean
  drugCatalogItemIds?: string[]
  onSelectBatch?: (batchId: string, consumableId?: string) => void
}

export function AddBatchDialog({
  prescribedNameIds,
  isConsumable,
  drugCatalogItemIds,
  onSelectBatch,
}: AddBatchDialogProps) {
  const {t} = useTranslate('common')

  const form = useAppForm({
    defaultValues: {
      search: '',
    },
  })

  const debounceMs = 500
  const search = useDebouncedState(
    useStore(form.store, state => state.values.search),
    debounceMs
  )

  const [batches] = useSearchBatches({
    search,
    prescribedNameIds,
    isConsumable,
    drugCatalogItemIds,
  })

  const columns = useMemo<ColumnDef<Batch, unknown>[]>(
    () => [
      columnHelper.display({
        id: 'product',
        maxSize: 50,
        header: () => t('product'),
        cell: ({row}) => (
          <Text fontSize="sm" fontWeight="normal">
            {isConsumable ? row.original?.consumable?.name : row.original?.tradeName?.name}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'lotNumber',
        maxSize: 50,
        header: () => t('batch'),
        cell: ({row}) => (
          <Text fontSize="sm" fontWeight="normal" color="fg.muted">
            {row.original.lotNumber}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'expiryDate',
        maxSize: 20,
        header: () => t('expiry_date'),
        cell: ({row}) => (
          <Text fontSize="sm" fontWeight="normal" color="fg.muted">
            {getYearMonthFormatterUTC().format(new Date(row.original?.expiryDate))}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'quantity',
        maxSize: 20,
        header: () => t('qty'),
        cell: ({row}) => (
          <Text fontSize="sm" fontWeight="normal" color="fg.muted">
            {row.original.quantity}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        maxSize: 5,
        header: () => null,
        cell: ({row}) => (
          <Button
            variant={'ghost'}
            colorPalette={'cyan'}
            onClick={() => {
              onSelectBatch?.(row.original.id, row.original?.consumable?.id)
              onClose()
            }}
          >
            {t('select')}
          </Button>
        ),
      }),
    ],
    [t, isConsumable, onSelectBatch]
  )

  const table = useReactTable({
    data: batches,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
  })

  return (
    <>
      <DialogHeader>{t('add_substance_inventory')}</DialogHeader>

      <DialogBody minHeight="none" overflow="auto">
        <VStack gap="5" width="100%">
          <form.AppField name="search">
            {field => (
              <field.TextField
                inputGroupProps={{startElement: <Icon as={LuSearch} size="sm" />}}
                label={''}
                hideLabel
                placeholder={t('search_inventory.placeholder')}
              />
            )}
          </form.AppField>

          {batches.length > 0 ? <DataTable table={table} /> : <EmptyState title={t('no_batches')} />}
        </VStack>
      </DialogBody>
    </>
  )
}
